<template>
  <div class="mv-details">
    <div style="display: flex; align-items: center; gap: 2px">
      <button type="button" class="mv-tab" :class="{ 'is-on': tab === 'meta' }" @click="$emit('update:tab', 'meta')">Metadata</button>
      <button type="button" class="mv-tab" :class="{ 'is-on': tab === 'comment' }" @click="$emit('update:tab', 'comment')">Comment</button>
      <div v-if="tab === 'meta' && canEdit" style="margin-left: auto; display: flex; gap: 14px">
        <template v-if="editing">
          <button type="button" class="mv-link" style="color: var(--text-dim)" @click="editing = false">Cancel</button>
          <button type="button" class="mv-link" :disabled="saving" @click="save">Save</button>
        </template>
        <button v-else type="button" class="mv-link" @click="startEditing">Edit</button>
      </div>
    </div>

    <template v-if="tab === 'meta'">
      <template v-if="editing">
        <div style="display: flex; align-items: center; gap: 10px">
          <span class="mono mv-details-key">Metadata set</span>
          <div class="mv-select-wrap" style="flex: 0 1 260px; min-width: 0">
            <select class="mv-input" title="Metadata set" :value="setId" @change="chooseSet($event.target.value)">
              <option v-if="!setId" value="" disabled>Choose a set…</option>
              <option v-for="set in sets" :key="set.id" :value="set.id">{{ set.label }}</option>
            </select>
          </div>
        </div>
        <MetadataDisplay v-if="draft.length" :items="draft" />
        <span v-else style="font-size: 12px; color: var(--text-faint)">Choose a set to add its fields.</span>
        <span v-if="dropped.length" style="font-size: 11.5px; color: var(--warning)">Saving removes {{ dropped.join(", ") }}.</span>
      </template>
      <div v-else class="mv-details-grid">
        <div v-for="cell in cells" :key="cell.key" class="mv-details-cell">
          <span class="mono mv-details-key">{{ cell.key }}</span>
          <span class="mono mv-details-value" ellipsis :title="cell.value">{{ cell.value }}</span>
        </div>
      </div>
    </template>
    <textarea
      v-else
      ref="comment"
      class="mv-input"
      placeholder="Comment for the next clip…"
      :value="value"
      @input="$emit('input', $event.target.value)"></textarea>
  </div>
</template>

<script>
import { hasProxy } from "@/scripts/assetStatus";
import { toTc } from "@/scripts/timecode.js";
import { codecLabel } from "@/scripts/assetLabels.js";
import { isFilled, sameField, matchingSet, fieldsOfSet, plainCopy } from "@/scripts/metadataFields.js";

const NONE = "—";

/** A metadata field's value as one line of text. */
function fieldText(field) {
  switch (field.type) {
    case "TAG":
    case "MULTISELECT":
      return (field.arrayValue || []).join(", ");
    case "CHECKBOX":
      return field.booleanValue ? "Yes" : "No";
    case "DATE":
      return field.value || (field.dateValue ? new Date(field.dateValue).toISOString().slice(0, 10) : "");
    default:
      return field.value;
  }
}

// The Details panel under the clip bar: the asset's facts and metadata, or the next clip's comment.
export default {
  props: {
    asset: { type: Object, required: true },
    duration: { type: Number, default: 0 },
    tab: { type: String, default: "meta" },
    // the comment (note) of the next clip to be added
    value: { type: String, default: "" },
  },
  data() {
    // the draft keeps edits away from live updates until Save; pastValues feed a set switch
    return { editing: false, saving: false, draft: [], setId: "", pastValues: [] };
  },
  computed: {
    sets() {
      return this.$store.state.metadataSets || [];
    },
    /** Labels of the asset's filled fields the draft no longer has. */
    dropped() {
      const kept = (field) => this.draft.some((other) => sameField(other, field));
      return (this.asset.metadata || []).filter((field) => isFilled(field) && !kept(field)).map((field) => field.label);
    },
    canEdit() {
      return !!this.asset.id && this.$store.state.canEdit(this.asset);
    },
    cells() {
      const asset = this.asset;
      // a clip without a proxy plays the server's dummy file, whose length is not the asset's
      const duration = hasProxy(asset) && this.duration > 0 ? toTc(this.duration) : "";
      const format = asset.width && asset.height ? asset.width + "×" + asset.height + (asset.anamorphic ? " · anamorphic" : "") : "";
      const facts = [
        { key: "Duration", value: duration },
        { key: "Format", value: format },
        { key: "Codec", value: codecLabel(asset) },
        { key: "Tape ID", value: asset.tapeID },
        { key: "Created", value: asset.created ? new Date(asset.created).toISOString().slice(0, 10) : "" },
      ];
      const fields = (asset.metadata || []).map((field) => ({ key: field.label, value: fieldText(field) }));
      return facts.concat(fields).map((cell) => ({ key: cell.key, value: cell.value || NONE }));
    },
  },
  watch: {
    // by id: the asset object is replaced on every live update, which must not end an edit
    "asset.id"() {
      this.editing = false;
    },
  },
  methods: {
    /** Starts from the set the asset's fields come from, so fields the set gained since show up too. */
    startEditing() {
      const fields = this.asset.metadata || [];
      const set = matchingSet(this.sets, fields);
      this.setId = set ? set.id : "";
      this.draft = set ? fieldsOfSet(set, fields) : plainCopy(fields);
      this.pastValues = fields;
      this.editing = true;
    },
    // newest value first, so switching back and forth keeps what was typed
    chooseSet(id) {
      this.setId = id;
      this.pastValues = this.draft.concat(this.pastValues);
      this.draft = fieldsOfSet(this.sets.find((set) => set.id === id), this.pastValues);
    },
    save() {
      this.saving = true;
      this.$store.state.updateAsset({ id: this.asset.id, metadata: this.draft }, (ok) => {
        this.saving = false;
        if (ok) this.editing = false;
      });
    },
    focusComment() {
      this.$nextTick(() => this.$refs.comment && this.$refs.comment.focus());
    },
  },
};
</script>
